import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserOrders } from '../services/api';
import { Utils } from '../Utils';
import AlertBox from '../components/AlertBox';

const PastOrders = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (!userData) {
            navigate('/login');
            return;
        }
        const parsedUser = JSON.parse(userData);
        const roomId = parsedUser.rooms === '*' ? '101' : parsedUser.rooms?.split(',')[0];
        fetchOrders(roomId);
    }, [navigate]);

    const getStatusLabel = (status) => {
        if (status === 10) return 'Pending';
        if (status === 20) return 'Received';
        return '';
    };

    const fetchOrders = async (roomId) => {
        setLoading(true);
        try {
            // since = last 30 days
            const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
            const response = await getUserOrders(roomId, since);
            // Response is a flat list of line items; wrap either shape.
            const lineItems = Array.isArray(response) ? response : (response?.data || []);
            // Group line items into orders by their OrderedOn timestamp.
            const grouped = {};
            lineItems.forEach(item => {
                const key = item.OrderedOn;
                if (!grouped[key]) grouped[key] = [];
                grouped[key].push(item);
            });
            const orderList = Object.entries(grouped).map(([orderedOn, items]) => ({
                orderedOn,
                items,
                total: items.reduce(
                    (sum, it) => sum + (parseFloat(it.ProductPrice) || 0) * (it.Quantity || 0),
                    0
                )
            }));
            setOrders(orderList);
        } catch (err) {
            setAlertMessage('Failed to load orders: ' + (err.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            width: '100%',
            maxWidth: '480px',
            margin: '0 auto',
            minHeight: '100vh',
            background: '#ffffff'
        }}>
            {/* Header */}
            <header style={{
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f0f0f0',
                borderBottom: '1px solid #e0e0e0'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img
                        src="/dog_mascot.png"
                        alt="Logo"
                        style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'inline';
                        }}
                    />
                    <span style={{ fontSize: '24px', display: 'none' }}>🐕</span>
                    <h2 style={{
                        margin: 0,
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#000',
                        fontFamily: 'Arial, sans-serif'
                    }}>
                        Past Orders
                    </h2>
                </div>
                <button
                    onClick={() => navigate('/shopping')}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#AE3041',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '4px',
                        fontFamily: 'Arial, sans-serif'
                    }}
                >
                    ←
                </button>
            </header>

            <div style={{ padding: '16px' }}>
                {loading ? (
                    <p style={{ textAlign: 'center', color: '#666', fontFamily: 'Arial, sans-serif' }}>
                        Loading orders...
                    </p>
                ) : orders.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#666', fontFamily: 'Arial, sans-serif' }}>
                        No orders found.
                    </p>
                ) : (
                    orders.map((order, idx) => (
                        <div
                            key={order.orderedOn || idx}
                            style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '12px',
                                marginBottom: '12px'
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px',
                                paddingBottom: '8px',
                                borderBottom: '1px solid #e0e0e0'
                            }}>
                                <span style={{ fontWeight: 'bold', color: '#000', fontSize: '14px' }}>
                                    {Utils.formatDateTime(order.orderedOn)}
                                </span>
                                <span style={{ fontWeight: 'bold', color: '#AE3041', fontSize: '14px' }}>
                                    ${order.total.toFixed(2)}
                                </span>
                            </div>
                            <div>
                                {order.items.map((item, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            fontSize: '13px',
                                            color: '#000',
                                            padding: '4px 0'
                                        }}
                                    >
                                        <span>
                                            {item.ProductGroup}
                                            {item.ProductSKU ? ` — ${item.ProductSKU}` : ''}
                                            {getStatusLabel(item.Status) && (
                                                <span style={{
                                                    marginLeft: '8px',
                                                    fontSize: '11px',
                                                    fontWeight: '500',
                                                    color: item.Status === 20 ? '#2e7d32' : '#b26a00'
                                                }}>
                                                    ({getStatusLabel(item.Status)})
                                                </span>
                                            )}
                                        </span>
                                        <span style={{ color: '#666' }}>
                                            {item.Quantity} × ${item.ProductPrice}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}

                <AlertBox
                    show={alertMessage != null}
                    title="Alert"
                    message={alertMessage}
                    onOK={() => setAlertMessage(null)}
                />
            </div>
        </div>
    );
};

export default PastOrders;
